import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataChange } from 'devextreme/common/grids';
import Tabs, { Item } from 'devextreme-react/tabs';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Toast } from 'devextreme-react/toast';
import { formatMessage } from 'devextreme/localization';

import { Button, TextBox } from 'devextreme-react';

import MessageForm, { MessageFormHandle, ErrorMessage } from '../../components/message-form/MessageForm';
import PathApi from '../../api/PathApi';
import PathVersionStatus from '../../components/path-version-status/PathVersionStatus';
import ChangeInProgressForm from '../../components/change-in-progress-form/ChangeInProgressForm';

import SourceSelection from '../../components/source-selection/SourceSelection';
import FiltersSelection from '../../components/filters-selection/FiltersSelection';
import useFilteringCapabilities from '../../components/filters-selection/useFilteringCapabilities';
import { collectDeclaredParameters } from '../../components/filter-selection/FilterBuilderInline';

import DataTableExposedFields, { DataTableExposedFieldsHandle } from '../../components/datatable-exposed-fields/DataTableExposedFields';
import DataTableResume from '../../components/datatable-resume/DataTableResume';

import PathDataTableApi from '../../api/PathDataTableApi';

import GetPathDataTableDetailResult from '../../classes/api/result/pathdatatablemodule/GetPathDataTableDetailResult';
import PathDataTableDetailsResult from '../../classes/api/result/pathdatatablemodule/PathDataTableDetailsResult';
import PathDataTableMappingDto from '../../classes/dtos/pathdatatablemodule/PathDataTableMappingDto';

import DataTableReadDto from '../../classes/dtos/pathcommonmodule/DataTableReadDto';
import DataSourceDataTableReadDto from '../../classes/dtos/pathcommonmodule/DataSourceDataTableReadDto';
import PathDataTableReadDto from '../../classes/dtos/pathcommonmodule/PathDataTableReadDto';

import DataTableFieldMappingDto from '../../classes/dtos/pathdatatablemodule/DataTableFieldMappingDto';

import PathCheckResultDto from '../../classes/dtos/pathmodule/PathCheckResultDto';
import { checkPathBeforeSaveAndPublish, getPathPublishChecklist, PathBeforeSaveValidationInput, ValidationRow } from '../../logics/pathmodule/PathBeforeSaveValidation';
import { performPathSave } from '../../logics/pathmodule/performPathSave';
import { useAuth } from '../../contexts/auth';

import TransformationCapabilitiesDto from '../../classes/dtos/transformationmodule/TransformationCapabilitiesDto';
import TransformationNotConfigurableDto from '../../classes/dtos/transformationmodule/TransformationNotConfigurableDto';
import PathDataTableMappingCapabilitiesDto from '../../classes/dtos/pathdatatablemodule/PathDataTableMappingCapabilitiesDto';

import DataTypeDto from '../../classes/dtos/datastructuremodule/DataTypeDto';

import FieldResumeDto from '../../classes/dtos/pathcommonmodule/FieldResumeDto';


export default function PathDataTable() {

    const { id } = useParams();


    const { sessionDetail } = useAuth();
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const pathId = Number.parseInt(id ?? "0");

    // Feature flag BD (Feature.FilterVisibility) : première condition d'affichage de l'onglet "Filters".
    const filterTabsEnabled = sessionDetail?.WithFilterVisibility ?? false;

    const messageFormHandleRef = useRef<MessageFormHandle>(null);

    const dataTableExposedFieldsRef = useRef<DataTableExposedFieldsHandle>(null);

    const [pathDataTableDetail, setPathDataTableDetail] = useState<GetPathDataTableDetailResult | null>(null);

    // "Advanced mode" checkbox (FILTER column header). Page-level state, persisted on the mapping version.
    const [advancedFilterMode, setAdvancedFilterMode] = useState<boolean>(false);
    // Advanced mode is a one-way switch: once it has been saved it can't be turned back off.
    const [advancedFilterModeLocked, setAdvancedFilterModeLocked] = useState<boolean>(false);

    // L'onglet "Filters" (mode complexe) n'est disponible que si la feature BD est active ET que le mode avancé
    // est activé sur ce path — fusion du feature flag Feature.FilterVisibility et de l'ancien `!simpleFilterMode`.
    const filtersTabAvailable = filterTabsEnabled && advancedFilterMode;

    // Colonne FILTER des grilles : visible seulement si la table source gère le filtrage. Même calcul
    // (`withFilter`) que l'onglet "Filters" dans FiltersSelection, via le hook partagé.
    const filterSourceTableReferenceId = pathDataTableDetail?.Source instanceof DataSourceDataTableReadDto ? (pathDataTableDetail.Source.TableReferenceId ?? 0) : 0;
    const { withFilter: sourceHandlesFilters } = useFilteringCapabilities(filterSourceTableReferenceId);

    const [capabilities, setCapabilities] = useState<PathDataTableMappingCapabilitiesDto[]>([]);
    const [transformationsCapabilities, setTransformationsCapabilities] = useState<TransformationCapabilitiesDto[]>([]);

    const [mapping, setMapping] = useState<DataTableFieldMappingDto[]>([]);

    const MAX_TAB = 3;
    const FILTERS_TAB = 2;

    const [currentTab, setCurrentTab] = useState<number>(() => {
        const tabFromUrl = Number(searchParams.get('tab'));
        const isValid = !Number.isNaN(tabFromUrl) && tabFromUrl >= 0 && tabFromUrl <= MAX_TAB && (filterTabsEnabled || tabFromUrl !== FILTERS_TAB);
        return isValid ? tabFromUrl : 0;
    });

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const changeInProgress = useRef<boolean>(false);

    // Mirror of changeInProgress for rendering (the ref alone doesn't trigger a re-render).
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [isPublished, setIsPublished] = useState<boolean>(false);

    const markChanged = useCallback(() => {
        changeInProgress.current = true;
        setHasUnsavedChanges(true);
    }, []);

    const [editNameInprogress, setEditNameInprogress] = useState<string | null>(null);

    const dataChangesRef = useRef<DataChange[]>([]);

    const selectedIdsRef = useRef<number[]>([]);

    const pathDataTableMappingDtosRef = useRef<PathDataTableMappingDto[]>([]);

    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        type: 'info',
        message: '',
        withRedirect: false
    } as {
        isVisible: boolean,
        type: 'info' | 'error' | 'success',
        message: string,
        withRedirect: boolean
    });

    useEffect(() => {
        PathDataTableApi.GetPathDataTableDetailAsync(pathId)
            .then(detail => {
                if (detail.IsFailed()) {
                    navigate("/paths");
                }
                else {
                    setPathDataTableDetail(detail.Result as GetPathDataTableDetailResult);
                    setAdvancedFilterMode((detail.Result as GetPathDataTableDetailResult).AdvancedFilterMode);
                    setIsPublished((detail.Result as GetPathDataTableDetailResult).PublishedVersionId !== null && (detail.Result as GetPathDataTableDetailResult).PublishedVersionId === (detail.Result as GetPathDataTableDetailResult).LastVersionId);
                    setAdvancedFilterModeLocked((detail.Result as GetPathDataTableDetailResult).AdvancedFilterMode);
                }
            });
    }, [pathId, navigate]);

    const dataLoad = useCallback(async () => {

        if (pathDataTableDetail === null) {
            return;
        }

        if (pathDataTableDetail.Source === null) {
            return;
        }

        let tableReferenceId: number | null = null;
        let pathId: number | null = null;

        if (pathDataTableDetail.Source instanceof DataSourceDataTableReadDto) {
            if ((pathDataTableDetail.Source.DataSourceId ?? 0) > 0 && (pathDataTableDetail.Source.TableReferenceId ?? 0) > 0) {
                tableReferenceId = pathDataTableDetail.Source.TableReferenceId;
            }
        }

        if (pathDataTableDetail.Source instanceof PathDataTableReadDto) {
            if ((pathDataTableDetail.Source.PathId ?? 0) > 0) {
                pathId = pathDataTableDetail.Source.PathId;
            }
        }

        if (tableReferenceId !== null || pathId !== null) {

            let getPathDataTableCapabilitiesResult = await PathDataTableApi.GetPathDataTableCapabilitiesAsync(pathDataTableDetail.Source, pathDataTableDetail.LinkedDataTables.map((e) => e.LinkedTable));

            if (getPathDataTableCapabilitiesResult.IsFailed()) {
                alert("Error : " + getPathDataTableCapabilitiesResult.ErrorMessage);
                return;
            }

            setCapabilities(getPathDataTableCapabilitiesResult.Result.MappingsCapabilities);
            let transformationsCapabilitiesTemp = getPathDataTableCapabilitiesResult.Result.TransformationsCapabilities;
            transformationsCapabilitiesTemp.push(new TransformationNotConfigurableDto(0, "Direct", 0, new DataTypeDto(0, false, ""), new DataTypeDto(0, false, ""), "TransformationNotConfigurable"));
            setTransformationsCapabilities(transformationsCapabilitiesTemp);

            let Mappings = pathDataTableMappingDtosRef.current.length > 0
                ? pathDataTableMappingDtosRef.current.map(e => new PathDataTableMappingDto(e.FieldReferenceId, e.TransformationLogicId, e.TransformationParameters, e.AggregationGroupBy, e.AggregationLogicId, e.AggregationParameters, e.DisplayName, selectedIdsRef.current.includes(e.FieldReferenceId), e.Filters, e.DeclaredVariables))
                : (pathDataTableDetail?.Mappings ?? []);

            selectedIdsRef.current = Mappings.filter(e => e.IsSelected).map(e => e.FieldReferenceId);

            let mappingTemp = generateMapping(getPathDataTableCapabilitiesResult.Result.Fields, Mappings);

            setMapping(mappingTemp);

            let result = new Array<PathDataTableMappingDto>();

            mappingTemp.forEach((e: DataTableFieldMappingDto) => {
                let readMapping = Mappings.find(x => x.FieldReferenceId === e.SourceFieldReferenceId) ?? null;
                result.push(new PathDataTableMappingDto(e.SourceFieldReferenceId ?? 0, e.TransformationType ?? 0, e.TransformationParameters ?? "", e.AggregationGroupBy, e.AggregationType ?? 0, e.AggregationParameters ?? "", e.DisplayName, readMapping?.IsSelected ?? false, e.Filters, e.DeclaredVariables));
            });

            pathDataTableMappingDtosRef.current = result;
        } else {
            setCapabilities([]);
            setTransformationsCapabilities([]);
            setMapping([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathDataTableDetail?.Source]);

    useEffect(() => {
        dataLoad()
    }, [dataLoad]);

    function generateMapping(fields: PathDataTableDetailsResult[], mappings: PathDataTableMappingDto[]): DataTableFieldMappingDto[] {
        let all = fields.flatMap(x => {
            return x.Fields.map((e) => {
                let readMapping = mappings?.find(x => x.FieldReferenceId === e.Id) ?? null;
                if (readMapping === null) {
                    return new DataTableFieldMappingDto(e.Id, null, null, false, null, null, e.Name, e.Type, e.Name, x.DataTable.Label, []);
                } else {
                    return new DataTableFieldMappingDto(e.Id, readMapping.TransformationLogicId, readMapping.TransformationParameters, readMapping.AggregationGroupBy, readMapping.AggregationLogicId, readMapping.AggregationParameters, e.Name, e.Type, readMapping.DisplayName, x.DataTable.Label, readMapping.Filters, readMapping.DeclaredVariables);
                }
            });
        });

        return all.toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);
    }

    // The name is saved on its own (not part of the version): it never creates a version nor counts as an unsaved change.
    async function handleNameChange() {
        const newName = editNameInprogress;
        if (newName === null) {
            return;
        }

        const renameResult = await PathApi.RenamePathAsync(pathId, newName);
        if (renameResult.IsFailed()) {
            messageFormHandleRef.current?.Open(formatMessage("PathValidation_SaveFailed"), ErrorMessage);
            return;
        }

        setPathDataTableDetail((previous) => {
            if (previous === null) {
                return previous;
            }
            previous.Name = newName;
            return previous;
        });
        setEditNameInprogress(null);
        setToastConfig({
            ...toastConfig,
            isVisible: true,
            type: 'success',
            message: formatMessage('PathName_Saved'),
            withRedirect: false
        });
    }

    const handleDataChanges = useCallback((changes: DataChange[], origin: DataTableFieldMappingDto[]) => {
        dataChangesRef.current = changes;

        // Grid edits are unsaved changes too (the save buttons depend on it).
        if (changes.length > 0) {
            markChanged();
        }

        let result = new Array<PathDataTableMappingDto>();

        origin.forEach((e: DataTableFieldMappingDto) => {
            let existing = pathDataTableMappingDtosRef.current.find(x => x.FieldReferenceId === e.SourceFieldReferenceId) ?? null;
            result.push(new PathDataTableMappingDto(e.SourceFieldReferenceId, e.TransformationType ?? 0, e.TransformationParameters ?? "", e.AggregationGroupBy, e.AggregationType ?? 0, e.AggregationParameters ?? "", e.DisplayName, existing?.IsSelected ?? false, e.Filters, e.DeclaredVariables));
        });

        changes.forEach((e: DataChange) => {

            let row = result.find((x) => x.FieldReferenceId === e.key) ?? null;

            if (row !== null) {
                if (e.data.SourceFieldReferenceId !== undefined) {
                    row.FieldReferenceId = e.data.SourceFieldReferenceId;
                }
                if (e.data.TransformationType !== undefined) {
                    row.TransformationLogicId = e.data.TransformationType ?? 0;
                }
                if (e.data.TransformationParameters !== undefined) {
                    row.TransformationParameters = e.data.TransformationParameters;
                }
                if (e.data.AggregationGroupBy !== undefined) {
                    row.AggregationGroupBy = e.data.AggregationGroupBy;
                }
                if (e.data.AggregationType !== undefined) {
                    row.AggregationLogicId = e.data.AggregationType;
                }
                if (e.data.AggregationParameters !== undefined) {
                    row.AggregationParameters = e.data.AggregationParameters;
                }
                if (e.data.DisplayName !== undefined) {
                    row.DisplayName = e.data.DisplayName;
                }
                if (e.data.Filters !== undefined) {
                    row.Filters = e.data.Filters;
                }
                if (e.data.DeclaredVariables !== undefined) {
                    row.DeclaredVariables = e.data.DeclaredVariables;
                }
            }
        });

        pathDataTableMappingDtosRef.current = result;

    }, []);

    const handleSelectionChanges = useCallback((ids: number[]) => {
        selectedIdsRef.current = ids;
        markChanged();
    }, []);

    const handleAdvancedFilterModeChange = useCallback((value: boolean) => {
        if (advancedFilterModeLocked && !value) {
            return;
        }
        setAdvancedFilterMode(value);
        markChanged();
    }, [advancedFilterModeLocked]);

    const handleDataSourceRefreshed = useCallback(() => {
        dataLoad();
    }, [dataLoad]);

    const handleTableMappingChange = useCallback((tableMappingReadDto: DataTableReadDto) => {
        setPathDataTableDetail((previous) => {
            if (previous === null) {
                return previous;
            }

            markChanged();

            return new GetPathDataTableDetailResult(
                previous.Id,
                previous.Name,
                tableMappingReadDto,
                previous.LinkedDataTables,
                previous.Mappings,
                previous.Schedule,
                previous.LastVersionId,
                previous.PublishedVersionId,
                previous.AdvancedFilterMode);
        });

    }, []);

    function GetPathDataTableMappingCapabilities(fieldId: number) {
        return capabilities.find((e) => e.SourceFieldId === fieldId) ?? null;
    }

    function GetFieldName(fieldId: number) {
        return mapping.find((e) => e.SourceFieldReferenceId === fieldId)?.Name ?? "";
    }

    function buildPathValidationInput(): PathBeforeSaveValidationInput {
        const selected = pathDataTableMappingDtosRef.current
            .filter((e) => selectedIdsRef.current.includes(e.FieldReferenceId));

        const selectedRows: ValidationRow[] = selected.map((e) => ({
            labelFieldId: e.FieldReferenceId,
            transformationLogicId: e.TransformationLogicId,
            transformationParameters: e.TransformationParameters,
            fieldName: e.DisplayName,
            aggregationGroupBy: e.AggregationGroupBy,
            aggregationLogicId: e.AggregationLogicId,
            aggregationParameters: e.AggregationParameters,
        }));

        return {
            name: pathDataTableDetail?.Name,
            source: pathDataTableDetail?.Source,
            selectedRows,
            fieldNameMode: 'free',
            withAggregation: selected.some((e) => e.AggregationGroupBy),
            transformationsCapabilities,
            getAllowedTransformationLogicIds: (row) =>
                GetPathDataTableMappingCapabilities(row.labelFieldId)?.TransformationLogicIds ?? null,
            getFieldLabel: (fieldId) => GetFieldName(fieldId),
        };
    }

    function CheckBeforeSaveAndPublish(): PathCheckResultDto {
        return checkPathBeforeSaveAndPublish(buildPathValidationInput());
    }

    async function save(publish: boolean): Promise<boolean> {
        const detail = pathDataTableDetail;
        if (detail === null || detail.Source === null || messageFormHandleRef.current === null) {
            messageFormHandleRef.current?.Open(formatMessage("PathValidation_TechnicalError"), ErrorMessage);
            return false;
        }
        const source = detail.Source;

        const allMappings = pathDataTableMappingDtosRef.current.map(e => new PathDataTableMappingDto(e.FieldReferenceId, e.TransformationLogicId, e.TransformationParameters, e.AggregationGroupBy, e.AggregationLogicId, e.AggregationParameters, e.DisplayName, selectedIdsRef.current.includes(e.FieldReferenceId), e.Filters, e.DeclaredVariables));

        return performPathSave({
            messageForm: messageFormHandleRef.current,
            checkResult: CheckBeforeSaveAndPublish(),
            checklist: getPathPublishChecklist(buildPathValidationInput()),
            publish,
            persist: () => PathDataTableApi.UpdatePathDataTableDetailAsync(pathId, detail.Name, allMappings, source, detail.LinkedDataTables, detail.Schedule, advancedFilterMode, publish),
            onSuccess: (isPublished) => {
                dataTableExposedFieldsRef.current?.Save();
                if (advancedFilterMode) {
                    setAdvancedFilterModeLocked(true);
                }
                changeInProgress.current = false;
                setHasUnsavedChanges(false);
                setIsPublished(isPublished);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'success',
                    message: publish ? 'Path saved and published' : 'Path saved as draft',
                    withRedirect: false
                });
            },
        });
    }

    const onHiding = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false,
        });

        if (toastConfig.withRedirect) {
            navigate("/paths");
        }

    }, [toastConfig, navigate]);

    function cancel(): void {

        if (changeInProgress.current) {
            setIsOpen(true);
        }
        else {
            navigate("/paths");
        }
    }

    function cancelFromModal(): void {

        setIsOpen(false);
        navigate("/paths");
    }

    async function saveDraftFromModal(): Promise<void> {

        setIsOpen(false);
        if (await save(false)) {
            navigate("/paths");
        }
    }


    function changePage(index: number): void {
        // Onglet "Filters" indisponible (feature off ou mode avance desactive) : on l'enjambe dans le sens de navigation.
        const target = !filtersTabAvailable && index === FILTERS_TAB
            ? (index > currentTab ? index + 1 : index - 1)
            : index;
        setCurrentTab(target);
        setSearchParams({ tab: target.toString() }, { replace: true });
    };

    function back() {
        changePage(currentTab - 1);
    }

    function next() {
        changePage(currentTab + 1);
    }

    if (pathDataTableDetail === null) {
        return (<div>Waiting</div>);
    }

    function GetFieldsResume(): FieldResumeDto[] {
        let fieldsResume = new Array<FieldResumeDto>();
        pathDataTableMappingDtosRef.current.forEach((e) => {
            fieldsResume.push(new FieldResumeDto(
                e.FieldReferenceId,
                GetFieldName(e.FieldReferenceId),
                e.DisplayName));
        });
        return fieldsResume;
    }

    // Single "simple vs complex" filter state for the page: simple => the per-row FILTER column is
    // the editor and the Filters tab is hidden; complex => the reverse.
    const enterpriseMode = sessionDetail?.EnterpriseMode ?? false;

    return (
        <React.Fragment>
            <div style={{ width: '100%', height: '100%', padding: 24, flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start', display: 'inline-flex' }}>
                <div style={{ alignSelf: 'stretch', height: '100%', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 24, display: 'flex' }}>
                    <div style={{ alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex', minHeight: '49px' }}>
                        {editNameInprogress === null ?
                            <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 20, display: 'flex' }}>
                                <div style={{ color: '#E3E3E8', fontSize: 24, fontFamily: 'Manrope', fontWeight: '600', lineHeight: '36px', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                                    {pathDataTableDetail?.Name ?? "???"}
                                </div>
                                <img src="svgs/edit.svg" alt="circle" style={{ cursor: 'pointer' }} onClick={() => setEditNameInprogress(pathDataTableDetail?.Name ?? "???")} />
                            </div> :
                            <div style={{ width: '50%', justifyContent: 'flex-start', alignItems: 'center', gap: 10, display: 'flex' }}>
                                <TextBox style={{ width: '50%' }} defaultValue={pathDataTableDetail?.Name ?? "???"} onValueChange={(e) => setEditNameInprogress(e)} />
                                <Button icon="check" onClick={() => handleNameChange()} />
                                <Button icon="close" onClick={() => setEditNameInprogress(null)} />
                            </div>}
                        <Button text="Close" type="normal" stylingMode="text" onClick={() => cancel()} />
                    </div>
                    <Tabs width='100%' stylingMode='secondary' onSelectedIndexChange={changePage} selectedIndex={currentTab} >
                        <Item text="Source">
                        </Item>
                        <Item text="Exposed field(s)">
                        </Item>
                        <Item text="Filters" visible={filtersTabAvailable} disabled={pathDataTableDetail.Source instanceof PathDataTableReadDto}>
                        </Item>
                        <Item text="Checking">
                        </Item>
                    </Tabs>
                    {(currentTab === 0) ? <SourceSelection OriginTableId={null} OriginPathId={null} Direction="Source" WithDataTable={false} TableMapping={pathDataTableDetail.Source ?? null} SetTableMapping={handleTableMappingChange} DataSourceRefreshed={handleDataSourceRefreshed} WithCombined={true} LinkedDataTables={pathDataTableDetail.LinkedDataTables} /> : ""}
                    {(currentTab === 1) ? <DataTableExposedFields
                        ref={dataTableExposedFieldsRef}
                        MappingChanges={dataChangesRef.current}
                        SetMappingChanges={handleDataChanges}
                        Capabilities={capabilities}
                        TransformationsCapabilities={transformationsCapabilities}
                        Mapping={mapping}
                        IsSimpleFilterMode={enterpriseMode}
                        FiltersEnabled={filterTabsEnabled && sourceHandlesFilters}
                        TableReferenceId={(pathDataTableDetail.Source as DataSourceDataTableReadDto)?.TableReferenceId ?? 0}
                        AdvancedFilterMode={advancedFilterMode}
                        SetAdvancedFilterMode={handleAdvancedFilterModeChange}
                        AdvancedFilterModeLocked={advancedFilterModeLocked}
                        SelectionIds={selectedIdsRef.current}
                        SetSelectionIds={handleSelectionChanges}
                        MultipleTables={pathDataTableDetail.LinkedDataTables.length > 0} /> : ""}
                    {(currentTab === 2 && filtersTabAvailable) ? <FiltersSelection TableMapping={pathDataTableDetail.Source ?? null} SetTableMapping={handleTableMappingChange} WithSimpleParameters={false} DeclaredParameters={collectDeclaredParameters(pathDataTableMappingDtosRef.current)} SelectedFieldReferenceIds={selectedIdsRef.current} /> : ""}
                    {(currentTab === 3) ? <DataTableResume Source={pathDataTableDetail.Source ?? null} Mapping={GetFieldsResume()} SelectionIds={selectedIdsRef.current ?? []} /> : ""}
                </div>
                <div style={{ width: '100%', height: 64, position: 'absolute', bottom: 32, left: 0, display: 'flex', justifyContent: 'space-between', padding: '16px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Button icon='back' text="Back to previous step" type="normal" stylingMode="text" onClick={() => back()} disabled={currentTab === 0} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        <PathVersionStatus HasUnsavedChanges={hasUnsavedChanges} IsPublished={isPublished} />
                        <Button text="Save as draft" type="normal" stylingMode="text" onClick={() => save(false)} disabled={!hasUnsavedChanges} />
                        {currentTab === MAX_TAB ?
                            <Button text="Save and publish" type="default" stylingMode="contained" onClick={() => save(true)} disabled={isPublished && !hasUnsavedChanges} /> :
                            <Button text="Continue to next step" type="default" stylingMode="contained" onClick={() => next()} />}
                    </div>
                </div>
            </div>
            <ChangeInProgressForm IsOpen={isOpen} SetIsOpen={setIsOpen} Ok={() => saveDraftFromModal()} Cancel={() => cancelFromModal()} />
            <MessageForm ref={messageFormHandleRef} />
            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                type={toastConfig.type}
                onHiding={onHiding}
                displayTime={1000}
            />
        </React.Fragment>
    )
}
